// src/stores/courseStore.js
import { defineStore } from 'pinia';
import { findCalculationModuleById, getCalculationModuleById } from '@/config/calculationModules';
import { resetCourseWorkflowState } from '@/utils/resetCourseWorkflowState';
import { normalizeCourseId, courseIdsEqual } from '@/utils/courseId';
import { restoreAdaptiveRtDraftForActiveCourse } from '@/utils/adaptiveRtDraftPersist';
import { isAdaptiveRtModule, normalizeModuleId } from '@/utils/adaptiveRtModule';

export const HISTORY_REPORT_SCOPE_CURRENT = 'current_course';
export const HISTORY_REPORT_SCOPE_ALL = 'all_courses';

export const useCourseStore = defineStore('courseStore', {
  state: () => ({
    selectedCourseId: null,
    selectedCourse: null,
    activeCalculationModuleId: null,
    historyReportListScope: HISTORY_REPORT_SCOPE_CURRENT,
  }),
  getters: {
    isHistoryReportFilteredByCurrentCourse(state) {
      return state.historyReportListScope === HISTORY_REPORT_SCOPE_CURRENT;
    },
    activeCalculationModuleLabel(state) {
      if (!state.activeCalculationModuleId) return null;
      const mode = getCalculationModuleById(state.activeCalculationModuleId);
      return mode?.label || state.activeCalculationModuleId;
    },
  },
  actions: {
    setActiveCalculationModule(modeId) {
      if (!modeId) {
        this.activeCalculationModuleId = null;
        return;
      }
      const mode = findCalculationModuleById(modeId);
      this.activeCalculationModuleId = mode?.id ?? null;
    },
    clearActiveCalculationModule() {
      this.activeCalculationModuleId = null;
    },
    setHistoryReportListScope(scope) {
      if (scope === HISTORY_REPORT_SCOPE_ALL || scope === HISTORY_REPORT_SCOPE_CURRENT) {
        this.historyReportListScope = scope;
      }
    },
    resetHistoryReportListScope() {
      this.historyReportListScope = HISTORY_REPORT_SCOPE_CURRENT;
    },
    _resetWorkflowOnCourseChange() {
      resetCourseWorkflowState();
      this.historyReportListScope = HISTORY_REPORT_SCOPE_CURRENT;
    },
    _tryRestoreAdaptiveRtDraftAfterCourseSet(courseId) {
      if (courseId != null && isAdaptiveRtModule(this.activeCalculationModuleId)) {
        restoreAdaptiveRtDraftForActiveCourse({ skipIfStagesHavePlans: true });
      }
    },
    setCourse(course) {
      const nextId = normalizeCourseId(course?.id);
      if (course != null && nextId == null) {
        return;
      }
      const isCourseChange = !courseIdsEqual(nextId, this.selectedCourseId);
      if (isCourseChange && this.selectedCourseId != null) {
        this._resetWorkflowOnCourseChange();
      }
      this.selectedCourseId = nextId;
      this.selectedCourse = course || null;
      if (course?.latest_mode) {
        this.setActiveCalculationModule(normalizeModuleId(course.latest_mode));
      }
      this._tryRestoreAdaptiveRtDraftAfterCourseSet(nextId);
    },
    setCourseId(id) {
      const nextId = normalizeCourseId(id);
      if (!courseIdsEqual(nextId, this.selectedCourseId) && this.selectedCourseId != null) {
        this._resetWorkflowOnCourseChange();
      }
      this.selectedCourseId = nextId;
      this.selectedCourse = null;
      this._tryRestoreAdaptiveRtDraftAfterCourseSet(nextId);
    },
    clearCourse() {
      if (this.selectedCourseId != null) {
        this._resetWorkflowOnCourseChange();
      }
      this.selectedCourseId = null;
      this.selectedCourse = null;
    },
  },
});
